
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import certificateService from "../services/certificateService";

const INITIAL_FORM = {
  certificate_type_id: "",
  certificate_name: "",
  certificate_number: "",
  issuing_authority: "",
  issued_to: "",
  branch: "",
  issue_date: "",
  expiry_date: "",
  asset_reference_id: "",
  remarks: "",
};

const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px",
    background: "#f5f3ff",
    color: "#1f2937",
    fontFamily: "Arial, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "22px",
  },
  title: {
    margin: 0,
    color: "#4c1d95",
    fontSize: "25px",
    fontWeight: 700,
  },
  subtitle: {
    margin: "7px 0 0",
    color: "#6b7280",
    fontSize: "13px",
  },
  card: {
    maxWidth: "1100px",
    margin: "0 auto",
    background: "#ffffff",
    border: "1px solid #e9d5ff",
    borderRadius: "14px",
    boxShadow: "0 5px 20px rgba(76, 29, 149, 0.07)",
    overflow: "visible",
  },
  section: {
    padding: "24px",
    borderBottom: "1px solid #ede9fe",
  },
  sectionTitle: {
    margin: "0 0 20px",
    color: "#6d28d9",
    fontSize: "16px",
    fontWeight: 700,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "18px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
    minWidth: 0,
  },
  label: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "42px",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "7px",
    background: "#ffffff",
    color: "#111827",
    fontSize: "14px",
    outlineColor: "#8b5cf6",
  },
  readOnly: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "42px",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "7px",
    background: "#f9fafb",
    color: "#6b7280",
    fontSize: "14px",
  },
  error: {
    color: "#dc2626",
    fontSize: "12px",
    margin: 0,
  },
  notice: {
    padding: "12px 14px",
    marginBottom: "18px",
    borderRadius: "8px",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    fontSize: "14px",
  },
  dropdown: {
    position: "relative",
  },
  dropdownList: {
    position: "absolute",
    zIndex: 20,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: "220px",
    overflowY: "auto",
    background: "#ffffff",
    border: "1px solid #ddd6fe",
    borderRadius: "8px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
  },
  dropdownItem: {
    display: "block",
    width: "100%",
    padding: "11px 12px",
    textAlign: "left",
    background: "#ffffff",
    border: "none",
    borderBottom: "1px solid #f3f4f6",
    cursor: "pointer",
    color: "#374151",
    fontSize: "13px",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: "12px",
    padding: "20px 24px",
  },
  primaryButton: {
    padding: "11px 20px",
    border: "none",
    borderRadius: "8px",
    background: "linear-gradient(135deg, #7c3aed, #5b21b6)",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryButton: {
    padding: "11px 20px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    background: "#ffffff",
    color: "#374151",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  loading: {
    padding: "50px 20px",
    textAlign: "center",
    color: "#6d28d9",
    fontSize: "15px",
  },
  helpText: {
    color: "#6b7280",
    fontSize: "12px",
    margin: "4px 0 0",
  },
};

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
  readOnly = false,
  error = "",
}) {
  return (
    <div style={styles.field}>
      <label htmlFor={name} style={styles.label}>
        {label}
        {required ? " *" : ""}
      </label>

      <input
        id={name}
        name={name}
        type={type}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        readOnly={readOnly}
        style={readOnly ? styles.readOnly : styles.input}
      />

      {error ? <p style={styles.error}>{error}</p> : null}
    </div>
  );
}

function getDateValue(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function getCertificateTypeId(certificate) {
  return (
    certificate?.certificate_type_id ??
    certificate?.certificate_type?.id ??
    ""
  );
}

function getCertificateTypeName(certificate) {
  return (
    certificate?.certificate_type_name ??
    certificate?.certificate_type?.type_name ??
    certificate?.certificate_type?.name ??
    ""
  );
}

export default function CertificateEditPage() {
  const navigate = useNavigate();
  const { certificateId } = useParams();

  const [form, setForm] = useState(INITIAL_FORM);
  const [certificate, setCertificate] = useState(null);
  const [certificateTypes, setCertificateTypes] = useState([]);
  const [typeSearch, setTypeSearch] = useState("");
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadPage() {
      setLoading(true);
      setPageError("");

      try {
        const [certificateResult, typesResult] = await Promise.all([
          certificateService.getCertificate(certificateId),
          certificateService.listCertificateTypes(),
        ]);

        if (!active) return;

        const loadedCertificate =
          certificateResult?.data ?? certificateResult;

        const loadedTypes =
          typesResult?.data ?? typesResult;

        const types = Array.isArray(loadedTypes)
          ? loadedTypes
          : Array.isArray(loadedTypes?.items)
          ? loadedTypes.items
          : Array.isArray(loadedTypes?.certificate_types)
          ? loadedTypes.certificate_types
          : [];

        if (
          !loadedCertificate ||
          typeof loadedCertificate !== "object"
        ) {
          throw new Error("Certificate details could not be loaded.");
        }

        const typeId = getCertificateTypeId(loadedCertificate);

        const selectedType = types.find(
          (item) => String(item.id) === String(typeId)
        );

        const typeName =
          getCertificateTypeName(loadedCertificate) ||
          selectedType?.type_name ||
          selectedType?.name ||
          "";

        setCertificate(loadedCertificate);
        setCertificateTypes(types);
        setTypeSearch(typeName);

        setForm({
          certificate_type_id: typeId === "" ? "" : String(typeId),
          certificate_name: loadedCertificate.certificate_name ?? "",
          certificate_number: loadedCertificate.certificate_number ?? "",
          issuing_authority: loadedCertificate.issuing_authority ?? "",
          issued_to: loadedCertificate.issued_to ?? "",
          branch: loadedCertificate.branch ?? "",
          issue_date: getDateValue(loadedCertificate.issue_date),
          expiry_date: getDateValue(loadedCertificate.expiry_date),
          asset_reference_id: loadedCertificate.asset_reference_id ?? "",
          remarks: loadedCertificate.remarks ?? "",
        });
      } catch (error) {
        if (!active) return;

        setPageError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load certificate details."
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    if (certificateId) {
      loadPage();
    } else {
      setPageError("Certificate ID is missing.");
      setLoading(false);
    }

    return () => {
      active = false;
    };
  }, [certificateId]);

  const filteredTypes = useMemo(() => {
    const search = typeSearch.trim().toLowerCase();

    if (!search) return certificateTypes;

    return certificateTypes.filter((item) => {
      const name = String(item.type_name ?? item.name ?? "").toLowerCase();
      const code = String(item.type_code ?? "").toLowerCase();

      return name.includes(search) || code.includes(search);
    });
  }, [certificateTypes, typeSearch]);

  function handleChange(event) {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
      form: "",
    }));
  }

  function chooseType(item) {
    setForm((previous) => ({
      ...previous,
      certificate_type_id: String(item.id),
    }));

    setTypeSearch(item.type_name ?? item.name ?? "");
    setTypeDropdownOpen(false);

    setErrors((previous) => ({
      ...previous,
      certificate_type_id: "",
      form: "",
    }));
  }

  function validateForm() {
    const nextErrors = {};

    if (!form.certificate_type_id) {
      nextErrors.certificate_type_id = "Please select a certificate type.";
    }

    if (!form.certificate_name.trim()) {
      nextErrors.certificate_name = "Certificate name is required.";
    }

    if (
      form.issue_date &&
      form.expiry_date &&
      form.expiry_date < form.issue_date
    ) {
      nextErrors.expiry_date =
        "Expiry date cannot be earlier than the issue date.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setPageError("");

    if (!validateForm()) return;

    const payload = {
      certificate_type_id: Number(form.certificate_type_id),
      certificate_name: form.certificate_name.trim(),
      certificate_number: form.certificate_number.trim() || null,
      issuing_authority: form.issuing_authority.trim() || null,
      issued_to: form.issued_to.trim() || null,
      branch: form.branch.trim() || null,
      issue_date: form.issue_date || null,
      expiry_date: form.expiry_date || null,
      asset_reference_id: form.asset_reference_id.trim() || null,
      remarks: form.remarks.trim() || null,
    };

    setSaving(true);

    try {
      await certificateService.updateCertificate(
        certificateId,
        payload
      );

      navigate(`/assets/certificate-management/${certificateId}`, {
        state: { message: "Certificate updated successfully." },
      });
    } catch (error) {
      setPageError(
        error?.response?.data?.message ||
          "Unable to update certificate. Please try again."
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.loading}>Loading certificate details...</div>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.section}>
            <h2 style={styles.title}>Edit Certificate</h2>
            <p style={{ color: "#991b1b", marginTop: "16px" }}>
              {pageError || "Certificate could not be found."}
            </p>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() =>
                navigate("/assets/certificate-management")
              }
            >
              Back to Register
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ ...styles.header, maxWidth: "1100px", margin: "0 auto 22px" }}>
        <div>
          <h1 style={styles.title}>Edit Certificate</h1>
          <p style={styles.subtitle}>
            Update certificate information and validity details.
          </p>
        </div>

        <button
          type="button"
          style={styles.secondaryButton}
          onClick={() =>
            navigate(`/assets/certificate-management/${certificateId}`)
          }
        >
          Back to Certificate
        </button>
      </div>

      <form style={styles.card} onSubmit={handleSubmit}>
        {pageError ? (
          <div style={{ padding: "20px 24px 0" }}>
            <div style={styles.notice}>{pageError}</div>
          </div>
        ) : null}

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Certificate Information</h2>

          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Certificate Code</label>
              <div style={styles.readOnly}>
                {certificate.certificate_code || "—"}
              </div>
              <p style={styles.helpText}>
                Certificate code is generated by the system and cannot be edited.
              </p>
            </div>

            <div style={styles.field}>
              <label htmlFor="certificate_type_search" style={styles.label}>
                Certificate Type *
              </label>

              <div style={styles.dropdown}>
                <input
                  id="certificate_type_search"
                  type="text"
                  value={typeSearch}
                  onChange={(event) => {
                    setTypeSearch(event.target.value);
                    setTypeDropdownOpen(true);
                    setForm((previous) => ({
                      ...previous,
                      certificate_type_id: "",
                    }));
                    setErrors((previous) => ({
                      ...previous,
                      certificate_type_id: "",
                    }));
                  }}
                  onFocus={() => setTypeDropdownOpen(true)}
                  onBlur={() => {
                    setTimeout(() => setTypeDropdownOpen(false), 150);
                  }}
                  placeholder="Search certificate type..."
                  autoComplete="off"
                  style={styles.input}
                />

                {typeDropdownOpen ? (
                  <div style={styles.dropdownList}>
                    {filteredTypes.length ? (
                      filteredTypes.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          style={styles.dropdownItem}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => chooseType(item)}
                        >
                          <strong>
                            {item.type_name ?? item.name ?? "Unnamed type"}
                          </strong>
                          {item.type_code ? (
                            <span style={{ color: "#6b7280" }}>
                              {" "}({item.type_code})
                            </span>
                          ) : null}
                        </button>
                      ))
                    ) : (
                      <div style={{ padding: "12px", color: "#6b7280" }}>
                        No certificate types found.
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {errors.certificate_type_id ? (
                <p style={styles.error}>{errors.certificate_type_id}</p>
              ) : null}
            </div>

            <Field
              label="Certificate Name"
              name="certificate_name"
              value={form.certificate_name}
              onChange={handleChange}
              required
              placeholder="Enter certificate name"
              error={errors.certificate_name}
            />

            <Field
              label="Certificate Number"
              name="certificate_number"
              value={form.certificate_number}
              onChange={handleChange}
              placeholder="Enter certificate number"
            />

            <Field
              label="Issuing Authority"
              name="issuing_authority"
              value={form.issuing_authority}
              onChange={handleChange}
              placeholder="Enter issuing authority"
            />

            <Field
              label="Issued To"
              name="issued_to"
              value={form.issued_to}
              onChange={handleChange}
              placeholder="Enter issued-to details"
            />

            <Field
              label="Branch / Location"
              name="branch"
              value={form.branch}
              onChange={handleChange}
              placeholder="Enter branch or location"
            />

            <Field
              label="Asset Reference ID"
              name="asset_reference_id"
              value={form.asset_reference_id}
              onChange={handleChange}
              placeholder="Enter asset reference ID"
            />
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Validity Details</h2>

          <div style={styles.grid}>
            <Field
              label="Issue Date"
              name="issue_date"
              type="date"
              value={form.issue_date}
              onChange={handleChange}
            />

            <Field
              label="Expiry Date"
              name="expiry_date"
              type="date"
              value={form.expiry_date}
              onChange={handleChange}
              error={errors.expiry_date}
            />
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Additional Details</h2>

          <div style={styles.field}>
            <label htmlFor="remarks" style={styles.label}>
              Remarks
            </label>

            <textarea
              id="remarks"
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              rows={4}
              placeholder="Enter any additional remarks"
              style={{
                ...styles.input,
                minHeight: "100px",
                resize: "vertical",
              }}
            />
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Certificate Document</h2>
          <p style={{ margin: 0, fontSize: "14px", color: "#4b5563" }}>
            The existing certificate document will remain unchanged when you
            save these edits. Document replacement can be handled separately.
          </p>
        </section>

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.secondaryButton}
            disabled={saving}
            onClick={() =>
              navigate(`/assets/certificate-management/${certificateId}`)
            }
          >
            Cancel
          </button>

          <button
            type="submit"
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.7 : 1,
              cursor: saving ? "not-allowed" : "pointer",
            }}
            disabled={saving}
          >
            {saving ? "Saving..." : "Update Certificate"}
          </button>
        </div>
      </form>
    </div>
  );
}
